import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Eye,
  Shield,
  Scale,
  Users,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Megaphone,
  Download,
  FileText,
  Lock,
  Unlock,
  XCircle,
  Building2,
  DollarSign,
  Heart,
  Lightbulb,
  ExternalLink,
} from "lucide-react";

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#002B7F]/10 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-[#002B7F]/5 transition-colors"
      >
        <span className="font-bold text-[#002B7F] pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#C0C0C0] flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const faqs = [
  {
    question: "Does this ban NDAs?",
    answer:
      "No. This standard does not ban, restrict, or invalidate any NDA. NDAs remain a lawful tool for protecting legitimate business interests. This standard calls on public leaders and publicly funded organizations to voluntarily release NDA restrictions tied to misconduct — as a condition of public trust, not law.",
  },
  {
    question: "Is this a law or government mandate?",
    answer:
      "No. This is a public accountability standard enforced through voter expectations and civic pressure — not legislation. No one is compelled by government action. But the public is free to decide that leaders who hide behind secrecy do not deserve their trust, their votes, or their tax dollars.",
  },
  {
    question: "What about legitimate business NDAs?",
    answer:
      "Legitimate NDAs protecting trade secrets, intellectual property, and sensitive business information are not the focus. This standard targets NDAs that conceal misconduct — abuse, harassment, fraud, and behavior directly relevant to whether the public should trust a leader or fund an organization.",
  },
  {
    question: "Can someone be forced to release their NDAs?",
    answer:
      "No one is forced. But the public is not obligated to support, elect, or fund anyone who refuses basic transparency. If you want public authority or public money, the expectation of accountability is the price of entry.",
  },
  {
    question: "What if I already signed an NDA as a victim?",
    answer:
      "This standard supports your right to speak. NDAs cannot legally prevent reporting of illegal activity. This campaign strongly defends whistleblower protections and advocates that no agreement should ever silence someone from reporting harm or seeking justice.",
  },
];

const NDAReformPage = () => {
  return (
    <div data-testid="nda-reform-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-28 bg-[#002B7F]">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23D4AF37\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }}
          />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-[0.2em] mb-6">
            <Scale className="w-4 h-4" />
            NDA Accountability
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-6 max-w-3xl mx-auto">
            NDA Transparency & Accountability Standard
          </h1>
          <p className="text-lg text-[#C0C0C0] max-w-2xl mx-auto mb-10">
            If you hold public office, seek public office, or receive public
            funds, you should not be hiding behind silence agreements that
            shield misconduct from accountability.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#the-standard"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Read the Standard
            </a>
            <a
              href="#nda-release"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              View the Transparency Commitment
            </a>
          </div>

          {/* Video Overview */}
          <div className="mt-10 max-w-2xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Watch the Overview</p>
            <div className="relative aspect-video bg-black border border-white/20 overflow-hidden">
              <iframe
                src="https://www.youtube.com/embed/ekjcpcOXTOg?rel=0&modestbranding=1"
                title="NDA Transparency & Accountability - Cooper for Nevada"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid="nda-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── THE CORE ISSUE ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-6">
            No One in Public Life Should Be Protected From Accountability
          </h2>

          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <p className="text-slate-600 leading-relaxed mb-4">
                Non-Disclosure Agreements were designed for legitimate business
                protection — trade secrets, intellectual property, sensitive
                financial information. That is a necessary and lawful function.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                But in too many cases, NDAs are used to conceal misconduct tied
                to abuse, harassment, fraud, and unethical behavior that
                directly affects public trust.
              </p>
              <p className="text-slate-600 leading-relaxed">
                When this involves elected officials, candidates for office, or
                organizations receiving taxpayer funds, it is no longer a
                private matter. It is a{" "}
                <strong className="text-[#002B7F]">
                  public accountability issue.
                </strong>
              </p>
            </div>

            <div className="space-y-3">
              {[
                {
                  icon: Lock,
                  title: "Misconduct Hidden by Contract",
                  desc: "NDAs used to prevent victims and witnesses from speaking about abuse, harassment, and fraud",
                },
                {
                  icon: DollarSign,
                  title: "Silence Purchased by Power",
                  desc: "Those with resources settle quietly and continue patterns of behavior without consequence",
                },
                {
                  icon: XCircle,
                  title: "Public Trust Destroyed",
                  desc: "When leaders operate behind enforced secrecy, voters cannot make informed decisions",
                },
                {
                  icon: Building2,
                  title: "Taxpayer Funds in the Dark",
                  desc: "Organizations receiving public money shield misconduct from the people who fund them",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="flex items-start gap-3 p-4 bg-red-50 border border-red-200"
                  >
                    <Icon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-red-800 text-sm">
                        {item.title}
                      </p>
                      <p className="text-red-700 text-xs">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-10 bg-[#002B7F] p-6 text-center">
            <p className="text-white font-bold text-lg">
              "Public trust cannot coexist with hidden misconduct."
            </p>
          </div>
        </div>
      </section>

      {/* ── THE TRANSPARENCY STANDARD ── */}
      <section
        id="the-standard"
        className="py-16 md:py-24 bg-[#002B7F]/5 scroll-mt-32"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Scale className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              The Standard We Are Calling For
            </h2>
          </div>

          <div className="bg-white border-2 border-[#002B7F]/20 p-8 md:p-10 mb-8">
            <p className="text-[#002B7F] text-lg leading-relaxed font-bold mb-6">
              I am calling on every elected official, every candidate running
              for office, and every leader of any company that receives public
              funds, tax breaks, or government subsidies, to publicly release a
              broad NDA release for any agreements that conceal misconduct
              relevant to public trust.
            </p>
            <div className="w-16 h-1 bg-[#D4AF37] mx-auto mb-6" />
            <p className="text-[#002B7F] text-lg leading-relaxed font-bold">
              If you want access to public money or public authority, you must
              not be shielded from accountability by secrecy agreements that
              hide wrongdoing.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                icon: Users,
                title: "Elected Officials",
                desc: "Every person currently holding public office should release NDA restrictions tied to misconduct",
              },
              {
                icon: Megaphone,
                title: "Candidates for Office",
                desc: "Anyone seeking public authority must meet this transparency standard before asking for votes",
              },
              {
                icon: Building2,
                title: "Publicly Funded Organizations",
                desc: "Companies receiving contracts, tax breaks, or subsidies must not hide misconduct behind NDAs",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-[#002B7F] p-6">
                  <Icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                  <h3 className="font-bold text-white text-sm mb-2">
                    {item.title}
                  </h3>
                  <p className="text-white/70 text-xs leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── PUBLIC POWER STATEMENT ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Lightbulb className="w-10 h-10 text-[#002B7F] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              This Is What Public Power Means
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {[
              {
                title: "The Right to Decide",
                desc: "The public has the right to decide who they trust with their votes and their money",
              },
              {
                title: "No Obligation to Secrecy",
                desc: "Voters and citizens are not obligated to support leaders who hide behind silence agreements",
              },
              {
                title: "Public Responsibility",
                desc: "Public funding and public authority come with public responsibility — no exceptions",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-[#002B7F]/5 border-2 border-[#002B7F]/10 p-6"
              >
                <h3 className="font-bold text-[#002B7F] text-sm mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-xs leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-[#D4AF37]/15 border-2 border-[#D4AF37]/30 p-6 text-center">
            <p className="text-[#002B7F] font-bold text-lg">
              "We as the public do not owe support, votes, or trust to anyone
              who refuses basic transparency."
            </p>
          </div>
        </div>
      </section>

      {/* ── LEADING BY EXAMPLE ── */}
      <section
        id="nda-release"
        className="py-16 md:py-24 bg-[#002B7F] scroll-mt-32"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Eye className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Leading by Example
            </h2>
          </div>

          <div className="bg-white/10 border border-white/20 p-8 md:p-10 mb-8">
            <p className="text-white text-lg leading-relaxed mb-6">
              I have signed a broad NDA release allowing any individual who has
              ever signed an NDA involving me or any company I control to be
              released from those agreements — with no penalties, restrictions,
              or retaliation.
            </p>
            <p className="text-white/80 leading-relaxed mb-6">
              This is part of a broader commitment to transparency in public
              life. Leadership must begin with personal accountability. I
              cannot ask others to meet a standard I am not willing to meet
              myself.
            </p>
            <p className="text-[#D4AF37] font-bold text-lg">
              "If I expect transparency from others, I will demonstrate it
              myself."
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white/10 border border-white/20 p-5 text-center">
              <p className="text-3xl font-black text-[#D4AF37]">0</p>
              <p className="text-white/70 text-sm">
                NDAs used to conceal misconduct
              </p>
            </div>
            <div className="bg-white/10 border border-white/20 p-5 text-center">
              <p className="text-3xl font-black text-white">Public</p>
              <p className="text-white/70 text-sm">
                NDA release signed and posted
              </p>
            </div>
            <div className="bg-white/10 border border-white/20 p-5 text-center">
              <p className="text-3xl font-black text-white">100%</p>
              <p className="text-white/70 text-sm">
                Transparency commitment
              </p>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://customer-assets.emergentagent.com/job_solutions-nv/artifacts/hairq24v_NDA_Final_Signed.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-nda-release"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              <Download className="w-5 h-5" />
              View & Download the NDA Release
            </a>
            <a
              href="https://customer-assets.emergentagent.com/job_solutions-nv/artifacts/hhe6u35h_NDA_Template.docx"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              <ExternalLink className="w-5 h-5" />
              Use This as a Template
            </a>
          </div>
        </div>
      </section>

      {/* ── TAXPAYER FUNDS ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <DollarSign className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              Public Money Cannot Protect Private Silence
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              {
                title: "Government Contracts",
                desc: "Organizations awarded public contracts must not use NDAs to conceal misconduct affecting public trust",
              },
              {
                title: "Tax Incentives",
                desc: "Companies receiving tax breaks operate with public investment — that investment demands transparency",
              },
              {
                title: "Public Subsidies",
                desc: "Subsidized organizations must allow release of NDA restrictions tied to misconduct settlements",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-[#002B7F] p-6"
              >
                <h3 className="font-bold text-[#D4AF37] text-sm mb-2">
                  {item.title}
                </h3>
                <p className="text-white/70 text-xs leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-[#002B7F] p-6 text-center">
            <p className="text-white font-bold text-lg">
              "Taxpayer dollars do not purchase secrecy. They require
              accountability."
            </p>
          </div>
        </div>
      </section>

      {/* ── WHISTLEBLOWER PROTECTION ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Megaphone className="w-10 h-10 text-[#002B7F] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              No NDA Should Ever Silence the Truth
            </h2>
          </div>

          <div className="bg-white border-2 border-emerald-200 p-8 mb-8">
            <p className="text-slate-700 leading-relaxed mb-6">
              NDAs cannot override the right to report illegal activity. This
              standard strongly supports whistleblower protections. No one
              should be prevented from reporting harm or seeking justice —
              regardless of what they signed.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "Abuse of any kind",
                "Harassment in any form",
                "Fraud against individuals or the public",
                "Criminal behavior of any nature",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-slate-700 font-medium text-sm">
                    No NDA should silence reporting of: {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#002B7F] p-6 text-center">
            <p className="text-white font-bold text-lg">
              "The right to speak the truth about harm cannot be signed away."
            </p>
          </div>
        </div>
      </section>

      {/* ── ACCOUNTABILITY PRINCIPLE ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-8">
            Accountability Is Not Optional in Public Life
          </h2>

          <div className="grid sm:grid-cols-3 gap-4 mb-10">
            {[
              {
                title: "A Privilege, Not a Shield",
                desc: "Holding public office or receiving public funds is a privilege — not protection from accountability",
              },
              {
                title: "Baseline Expectation",
                desc: "Transparency is not extraordinary. It is the baseline expectation of leadership",
              },
              {
                title: "Secrecy Disqualifies Trust",
                desc: "Using secrecy to protect misconduct disqualifies any claim to public trust",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-[#002B7F]/5 border-2 border-[#002B7F]/10 p-5 text-left"
              >
                <h3 className="font-bold text-[#002B7F] text-sm mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-xs leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-[#002B7F] p-8">
            <p className="text-white font-bold text-lg">
              "If you cannot stand in the light, you do not belong in public
              service."
            </p>
          </div>
        </div>
      </section>

      {/* ── WHY THIS MATTERS ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <AlertTriangle className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              We Have Seen What Happens When Silence Protects Power
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/10 border border-white/20 p-6">
              <p className="text-white/90 leading-relaxed mb-4">
                High-profile cases like Jeffrey Epstein highlight how secrecy
                can enable harm for years — even decades — while victims are
                silenced and accountability is impossible.
              </p>
              <p className="text-white/90 leading-relaxed">
                Civil settlements involving public figures such as Bill Cosby
                show how NDAs and confidentiality can obscure accountability in
                the public eye — allowing patterns to continue unchecked.
              </p>
            </div>
            <div className="bg-white/10 border border-white/20 p-6 flex items-center">
              <div>
                <p className="text-[#D4AF37] font-bold text-lg leading-relaxed mb-4">
                  "These examples show why misconduct hidden behind legal
                  silence agreements is not just private — it becomes a public
                  trust issue."
                </p>
                <p className="text-white/70 text-sm">
                  When silence protects the powerful, everyone else pays the
                  price. This standard exists to change that equation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#002B7F] mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CLOSING ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl md:text-2xl font-bold text-[#002B7F] mb-6">
            We will not accept secrecy as a shield for misconduct.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed mb-2">
            Not in government. Not in business. Not in public life.
          </p>
          <p className="text-lg text-[#D4AF37] font-bold leading-relaxed">
            Accountability starts with standards. This is ours.
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Accountability Starts With Standards
          </h2>
          <p className="text-[#C0C0C0] mb-8">
            Support transparency. Demand accountability. Share this commitment.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Support the Standard
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
            <Link
              to="/policies/education"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              Protect Public Schools <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── SEO FOOTER ── */}
      <section className="py-6 bg-[#002B7F]/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-slate-400 text-center leading-relaxed">
            A political accountability initiative calling on elected officials,
            candidates, and publicly funded organizations to release NDA
            restrictions tied to misconduct, strengthen whistleblower
            protections, and uphold transparency as a condition of public trust.
          </p>
        </div>
      </section>
    </div>
  );
};

export default NDAReformPage;
